<script>
	import CostBreakdownTable from "src/components/CostBreakdownTable/CostBreakdownTable.svelte";
	import CustomCollapseCard from "src/components/CustomCollapseCard/CustomCollapseCard.svelte";
	import CustomInput from "src/components/CustomInput/CustomInput.svelte";
	import CustomSearch from "src/components/CustomSearch/CustomSearch.svelte";
	import CustomTextArea from "src/components/CustomTextArea/CustomTextArea.svelte";
	import DropdownCheckbox from "src/components/DropdownCheckbox/DropdownCheckbox.svelte";
	import { splitByComma } from "src/utils/misc";
	import {
		caseTypeOptions,
		claimTypeOptions,
		damageLocationOptions,
		liabilityOptions,
	} from "src/utils/options";
	import { Col, Row } from "@sveltestrap/sveltestrap";

	export let form;
	export let caseType;
	export let accidentGIAReportNo;
	export let claimType;
	export let selectedSides;
	export let liabilityType;
	export let noOfRepairDays;
	export let actualRepairCost;
	export let remarks;
	export let costBreakdownItems;
	export let caseDetails = {};
	export let state = "view"; // possible values: "view", "edit", "create"
	export let tnsRemarks = "";

	let damageIsOpen = false;

	$: if (($caseType.value?.value || $caseType.value) !== "Accident") {
		$accidentGIAReportNo.value = "";
	}

	function updateAmount(value, index) {
		if (value.includes("e") || value.includes("-")) {
			value = undefined;
		}
		costBreakdownItems[index].value = parseFloat(value) || 0;
	}

	function handleLocationChange(value) {
		$selectedSides.value = value.selectedSides;
	}

	let options = damageLocationOptions.map((item) => ({
		label: item.label,
		id: item.value,
		selected: $selectedSides.value?.includes(item.value),
	}));
</script>

<CustomCollapseCard title="Quotation" isOpenDefault={true} customClass="mb-4">
	<Col xs={12}>
		<Row class="mb-4">
			<Col xs={4}>
				<Row>
					<Col>
						<label for="caseType" class="d-block">
							<span>
								Case type<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col>
						<CustomSearch
							isSearchable={false}
							items={caseTypeOptions}
							placeholder="Select case type"
							bind:value={$caseType.value}
							error={$form.hasError("caseType.required")}
							errorText="Case type is required"
						/>
					</Col>
				</Row>
			</Col>
			{#if $caseType.value?.value === "Accident"}
				<Col xs={4}>
					<Row>
						<Col>
							<label for="accidentGIAReportNo" class="d-block">
								<span> Accident GIA Report No </span>
							</label>
						</Col>
					</Row>
					<Row>
						<Col>
							<CustomInput
								type="text"
								bind:value={$accidentGIAReportNo.value}
								placeholder="Eg. SJ0G257U000C"
								maxlength={50}
							/>
						</Col>
					</Row>
				</Col>
				<Col xs={4} />
			{/if}
			<Col xs={4}>
				<Row>
					<Col>
						<label for="claimType" class="d-block">
							<span>
								Type of claim<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col>
						<CustomSearch
							isSearchable={false}
							items={claimTypeOptions}
							placeholder="Select claim type"
							bind:value={$claimType.value}
							error={$form.hasError("claimType.required")}
							errorText="Claim type is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col xs={4}>
				<Row>
					<Col>
						<label for="selectedSides" class="d-block">
							<span>
								Damage location<span class="text-danger">*</span
								>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col>
						<DropdownCheckbox
							categoryName={$selectedSides.value?.length > 0
								? `${splitByComma($selectedSides.value)}`
								: "Select damage location"}
							{options}
							id="selectedSides"
							bind:selectedValues={$selectedSides.value}
							isOpen={damageIsOpen}
							toggle={() => (damageIsOpen = !damageIsOpen)}
							refetch={handleLocationChange}
						/>
					</Col>
				</Row>
			</Col>

			<Col xs={4}>
				<Row>
					<Col>
						<label for="liabilityType" class="d-block">
							<span>
								Liability status<span class="text-danger"
									>*</span
								>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col>
						<CustomSearch
							isSearchable={false}
							items={liabilityOptions}
							placeholder="Select liability status"
							bind:value={$liabilityType.value}
							error={$form.hasError("liabilityType.required")}
							errorText="Liability status is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col xs={4}>
				<Row>
					<Col>
						<label for="noOfRepairDays" class="d-block">
							<span>
								Number of Repair Days<span class="text-danger"
									>*</span
								>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col>
						<CustomInput
							type="number"
							bind:value={$noOfRepairDays.value}
							placeholder="Eg. 3"
							error={$form.hasError("noOfRepairDays.required")}
							errorText={$form.hasError("noOfRepairDays.required")
								? "Number of repair days is required"
								: $form.hasError("noOfRepairDays.pattern")
									? "Number of repair days must be a valid number"
									: ""}
						/>
					</Col>
				</Row>
			</Col>
		</Row>
	</Col>
	<hr class="divider" />
	<Row class="mb-4">
		<Col xs={4}>
			<Row>
				<Col>
					<label for="actualRepairCost" class="d-block">
						<span>
							Actual Repair Cost ($)<span class="text-danger"
								>*</span
							>
						</span>
					</label>
				</Col>
			</Row>
			<Row>
				<Col>
					<CustomInput
						type="number"
						bind:value={$actualRepairCost.value}
						placeholder="Eg. 5000.00"
						error={$form.hasError("actualRepairCost.required")}
						errorText={$form.hasError("actualRepairCost.required")
							? "Actual repair cost is required"
							: $form.hasError("actualRepairCost.pattern")
								? "Actual repair cost must be a valid number with up to 2 decimal places"
								: ""}
					/>
				</Col>
			</Row>
		</Col>
	</Row>

	<Row class="mb-4">
		<Row>
			<Col>
				<label for="costBreakdown" class="d-block mb-1">
					<span>Cost Breakdown<span class="text-danger">*</span></span
					>
				</label>
				<CostBreakdownTable
					items={costBreakdownItems}
					{updateAmount}
					isEditable={true}
				/>
			</Col>
		</Row>
		{#if caseDetails.tsRemarks && caseDetails.status === "PreBilling"}
			<Row>
				<Col>
					<div class="description">T&S Remarks</div>
					<div class="value">{caseDetails.tsRemarks}</div>
				</Col>
			</Row>
			<hr class="divider" />
		{/if}
		{#if state === "create" || caseDetails.status === "PreBilling" || caseDetails.status === "Quoting"}
			<Row>
				<Col xs={12}>
					<label for="remarks" class="d-block">
						<span>
							FQ Remarks<span class="text-danger">*</span>
						</span>
					</label>
					<CustomTextArea
						bind:value={$remarks.value}
						maxlength={3000}
						placeholder="Enter remarks"
						error={$form.hasError("remarks.required")}
						errorText={"Remarks is required."}
					/>
				</Col>
			</Row>
		{:else}
			<Row>
				<Col xs={12}>
					<label for="remarks" class="d-block">
						<span>
							T&S Remarks<span class="text-danger">*</span>
						</span>
					</label>
					<CustomTextArea
						bind:value={$tnsRemarks.value}
						maxlength={3000}
						placeholder="Enter remarks"
						error={$form.hasError("tnsRemarks.required")}
						errorText={"Remarks is required."}
					/>
					<hr class="divider" />
				</Col>
			</Row>
			<Row>
				<Col>
					<div class="description">FQ Remarks</div>
					<div class="value">{caseDetails.fqRemarks}</div>
				</Col>
			</Row>
		{/if}
	</Row>
</CustomCollapseCard>
